// Tamper-evidence for what a renter read and typed (temporary capture,
// odd/tasks/document-form-capture.md). The hashes computed here are a
// cross-check only: the server recomputes the authoritative ones from what it
// stores, so a forged browser value can only flag itself as a mismatch.
//
// The canonical text format is shared with the backend
// (`Core.Fleet.Acceptances`); keep both sides identical.

const encoder = new TextEncoder();

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(text));
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

function compareUtf8(a: Uint8Array, b: Uint8Array): number {
  const length = Math.min(a.length, b.length);
  for (let i = 0; i < length; i += 1) {
    if (a[i] !== b[i]) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

/**
 * One line per entry, sorted by the key's UTF-8 bytes:
 * `<key bytes>:<key>=<value bytes>:<value>\n`. Length prefixes make the text
 * unambiguous without any escaping, and avoid JSON canonicalization
 * differences between languages.
 */
export function canonicalFormDataText(values: Record<string, string>): string {
  return Object.entries(values)
    .map(([key, value]) => ({
      key,
      value,
      keyBytes: encoder.encode(key),
      valueBytes: encoder.encode(value),
    }))
    .sort((a, b) => compareUtf8(a.keyBytes, b.keyBytes))
    .map(
      (entry) =>
        `${entry.keyBytes.length}:${entry.key}=${entry.valueBytes.length}:${entry.value}\n`,
    )
    .join("");
}

export interface DocumentEvidence {
  content_sha256: string;
  form_data_sha256?: string;
  locale: string;
  timezone: string;
  viewport: string;
  first_viewed_at?: string;
  last_interaction_at?: string;
}

export interface DocumentViewTimes {
  first?: string;
  last?: string;
}

/** Evidence for every accepted document, keyed by document version id. */
export async function buildEvidenceForAcceptedDocuments({
  documents,
  acceptedVersionIds,
  valuesByVersion,
  timesByVersion,
}: {
  documents: { document_version_id: string; content: string }[];
  acceptedVersionIds: ReadonlySet<string>;
  valuesByVersion: Record<string, Record<string, string>>;
  timesByVersion: Record<string, DocumentViewTimes>;
}): Promise<Record<string, DocumentEvidence>> {
  const accepted = documents.filter((document) =>
    acceptedVersionIds.has(document.document_version_id),
  );
  const entries = await Promise.all(
    accepted.map(async (document) => {
      const id = document.document_version_id;
      const evidence = await buildDocumentEvidence({
        content: document.content,
        values: valuesByVersion[id],
        firstViewedAt: timesByVersion[id]?.first,
        lastInteractionAt: timesByVersion[id]?.last,
      });
      return [id, evidence] as const;
    }),
  );
  return Object.fromEntries(entries);
}

interface BuildDocumentEvidenceInput {
  content: string;
  values?: Record<string, string>;
  firstViewedAt?: string;
  lastInteractionAt?: string;
  locale?: string;
  timezone?: string;
  viewport?: string;
}

export async function buildDocumentEvidence({
  content,
  values,
  firstViewedAt,
  lastInteractionAt,
  locale = typeof navigator === "undefined" ? "" : navigator.language,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  viewport = typeof window === "undefined"
    ? ""
    : `${window.innerWidth}x${window.innerHeight}`,
}: BuildDocumentEvidenceInput): Promise<DocumentEvidence> {
  const hasValues = values !== undefined && Object.keys(values).length > 0;

  return {
    content_sha256: await sha256Hex(content),
    ...(hasValues
      ? { form_data_sha256: await sha256Hex(canonicalFormDataText(values)) }
      : {}),
    locale,
    timezone,
    viewport,
    ...(firstViewedAt ? { first_viewed_at: firstViewedAt } : {}),
    ...(lastInteractionAt ? { last_interaction_at: lastInteractionAt } : {}),
  };
}
